import {
  Fragment,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Combobox } from '@headlessui/react';
import { IconPlus, IconZoomOutline } from '@iwoca/icons';
import { Input, Spinner } from '@iwoca/orion';
import { Trans } from '@lingui/react/macro';
import clsx from 'clsx';
import { ButtonLink } from 'components/ButtonLink/ButtonLink';
import { PRIVACY_DISPLAY_CLASSES } from 'utils/privacyClasses';
import styles from './SearchableDropdown.module.css';

type TProps<TValue> = {
  searchQuery?: string;
  placeholder?: string;
  onSearch: (query: string) => void;
  onChange: (value: TValue) => void;
  options: TValue[];
  optionsLoading: boolean;
  optionRenderer: (value: TValue) => string;
  valueRenderer: (value: TValue) => string;
  onClear: () => void;
  inputClassName?: string;
  labelComponent: ReactNode;
  disabled?: boolean;
};

export function SearchableDropdown<TValue>({
  onSearch,
  onChange,
  searchQuery: initialSearchQuery,
  placeholder,
  options,
  optionsLoading,
  optionRenderer,
  valueRenderer,
  onClear,
  inputClassName,
  disabled = false,
  labelComponent,
}: TProps<TValue>) {
  const [inputValue, setInputValue] = useState('');

  const keywordInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (keywordInputRef.current) {
      if (initialSearchQuery) {
        keywordInputRef.current.value = initialSearchQuery;
      }
      keywordInputRef.current.disabled = disabled;
    }
  }, [initialSearchQuery, disabled]);

  useEffect(() => {
    onSearch(inputValue);
  }, [inputValue, onSearch]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && optionsLoading) {
      e.preventDefault();
    }
  };

  const handleOnClear = () => {
    setInputValue('');
    onClear();
    keywordInputRef.current!.value = '';
  };

  const renderOptions = () => {
    if (optionsLoading) {
      return (
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
      );
    }

    return (
      <>
        {options.length > 0 ? (
          options.map((option, index) => (
            <Combobox.Option
              className={({ active }) =>
                clsx(styles.option, { [styles.active]: active })
              }
              key={index}
              value={option}
            >
              {optionRenderer(option)}
            </Combobox.Option>
          ))
        ) : (
          <p className={styles.noResults}>
            <Trans>No results</Trans>
          </p>
        )}
      </>
    );
  };

  return (
    <Combobox onChange={onChange}>
      <div className={styles.companyInputLabel}>{labelComponent}</div>
      <div className={styles.companiesSearch}>
        <Combobox.Input
          onChange={(event) => {
            setInputValue(event.target.value);
          }}
          as={Fragment}
          displayValue={(item: TValue) => {
            if (item && inputValue) {
              return valueRenderer(item);
            }
            return '';
          }}
        >
          <Input
            iconRight={!inputValue ? <IconZoomOutline /> : undefined}
            placeholder={placeholder}
            className={clsx(styles.companyInput, inputClassName)}
            onKeyDown={handleKeyDown}
            ref={keywordInputRef}
          />
        </Combobox.Input>
        {inputValue && (
          <ButtonLink
            data-testid="clear-button"
            onClick={handleOnClear}
            type="button"
            className={styles.clearButton}
          >
            <IconPlus height="24" width="24" />
          </ButtonLink>
        )}
        {inputValue && (
          <Combobox.Options
            className={clsx(styles.options, PRIVACY_DISPLAY_CLASSES)}
          >
            {renderOptions()}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
}
